import * as React from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  ApiError, type AgentRecordContext, type AgentSharedTurnRequest, type AgentSharedTurnResult,
  getAgentSharedOperation, sendAgentSharedTurn, stopAgentSharedTurn,
  getListAgentSharedMessagesQueryKey, getListFileLibraryQueryKey, getListFileLocationsQueryKey,
  getGetDocumentWorkspaceQueryKey, getGetDocumentWorkspaceContextQueryKey, getListDocumentWorkspaceFoldersQueryKey,
  getListTenderFilesQueryKey, getGetManagedDocumentQueryKey
} from '@cdf/api-client-react';
import { sharedContextKey, sharedContextParams } from './shared-chat-context';
import { workspaceError } from './workspace-utils';

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** Store only the opaque recovery identity; no prompt, review, receipt or file content. */
function remembered(key: string) {
  try { const value = sessionStorage.getItem(key); return value && uuid.test(value) ? value : undefined; } catch { return undefined; }
}
export function useSharedAgentTurn(identity: string, context: AgentRecordContext, threadId: string) {
  const client = useQueryClient();
  const storageKey = 'agent-shared-operation:' + sharedContextKey(identity, context, threadId);
  const [requestId, setRequestId] = React.useState(() => remembered(storageKey));
  const initialRecoveryId = React.useRef(requestId);
  const [result, setResult] = React.useState<AgentSharedTurnResult>();
  const completed = React.useRef<AgentSharedTurnResult | undefined>(undefined);
  const [busy, setBusy] = React.useState(false);
  const [uncertain, setUncertain] = React.useState(Boolean(requestId));
  const [error, setError] = React.useState<string>();
  const [refreshNotice, setRefreshNotice] = React.useState<string>();
  const controller = React.useRef(new AbortController());
  const alive = React.useRef(true), flight = React.useRef(false), sequence = React.useRef(0);
  React.useEffect(() => {
    alive.current = true;
    if (controller.current.signal.aborted) controller.current = new AbortController();
    return () => { alive.current = false; sequence.current++; controller.current.abort(); flight.current = false; };
  }, []);
  function remember(id?: string) {
    setRequestId(id);
    try { if (id) sessionStorage.setItem(storageKey, id); else sessionStorage.removeItem(storageKey); } catch { /* Recovery remains available in memory. */ }
  }
  async function accept(value: AgentSharedTurnResult, token: number, failureMessage?: string) {
    if (!alive.current || sequence.current !== token) return;
    if (value.message.threadId !== threadId || value.clientRequestId !== (requestIdentity.current ?? requestId)) throw new Error('The original operation did not match this thread.');
    completed.current = value;
    const file = !value.message.restricted ? value.fileResult : undefined;
    setResult(value); setUncertain(value.message.status === 'running' || file?.state === 'unconfirmed');
    setError(file?.state === 'saved'
      ? value.message.status === 'completed' ? undefined : 'The file was saved. The chat did not finish; checking the original request will not save another copy.'
      : file?.state === 'unconfirmed' ? 'The file save is unconfirmed. Check the original request before another write; do not send it again.'
      : value.message.status === 'cancelled' ? 'Stopped. Check the original request for any work completed before Stop.'
      : value.message.status === 'failed' || value.message.status === 'interrupted'
        ? failureMessage ?? 'The response did not finish. Check the original request before deciding what to do next. Nothing is resent automatically.'
        : undefined);
    const keys: QueryKey[] = [getListAgentSharedMessagesQueryKey(threadId)];
    if (file?.state === 'saved') {
      keys.push(getListFileLibraryQueryKey(), getListFileLocationsQueryKey(),
        getGetDocumentWorkspaceQueryKey(file.workspaceId), getGetDocumentWorkspaceContextQueryKey(file.workspaceId),
        getListDocumentWorkspaceFoldersQueryKey(file.workspaceId));
      if (context.kind === 'tender') keys.push(getListTenderFilesQueryKey(context.recordId));
      const document = /^\/files\/([0-9a-f-]{36})$/i.exec(file.link);
      if (document) keys.push(getGetManagedDocumentQueryKey(document[1]!));
    }
    // Refresh errors cannot change a confirmed native save into a failed write.
    const refreshed = await Promise.allSettled(keys.map(queryKey => client.invalidateQueries({ queryKey }, { throwOnError: true })));
    if (alive.current && sequence.current === token) setRefreshNotice(refreshed.some(item => item.status === 'rejected')
      ? 'The result is retained, but a view could not refresh. Open Files or check current access and history.' : undefined);
  }
  const requestIdentity = React.useRef(requestId);
  async function run(action: (signal: AbortSignal) => Promise<AgentSharedTurnResult>, reconcileFailure = false) {
    if (flight.current) return false;
    flight.current = true; setBusy(true); setError(undefined); setRefreshNotice(undefined);
    const token = ++sequence.current;
    try {
      const value = await action(controller.current.signal);
      await accept(value, token);
      return alive.current && sequence.current === token && (value.message.status === 'completed' || value.fileResult?.state === 'saved');
    }
    catch (failure) {
      if (!alive.current || sequence.current !== token) return false;
      const failureMessage = failure instanceof ApiError && failure.code === 'AI_TIMEOUT'
        ? 'Agent timed out before completing an answer. Your text is retained; nothing will be resent automatically.'
        : workspaceError(failure);
      setUncertain(true); setError(failureMessage);
      // Read once after a lost/error response. A GET can reconcile the durable
      // outcome but must never dispatch another turn or another provider attempt.
      if (reconcileFailure && requestIdentity.current) {
        try {
          const original = await getAgentSharedOperation(threadId, requestIdentity.current, sharedContextParams(context), { signal: controller.current.signal });
          await accept(original, token, failureMessage);
          return alive.current && sequence.current === token && (original.message.status === 'completed' || original.fileResult?.state === 'saved');
        } catch {
          // An unavailable original stays uncertain and explicitly recoverable.
        }
      }
      return false;
    }
    finally { if (alive.current && sequence.current === token) { flight.current = false; setBusy(false); } }
  }
  function send(input: Omit<AgentSharedTurnRequest, 'context' | 'clientRequestId'>) {
    if (flight.current || uncertain) return Promise.resolve(false);
    setResult(undefined); completed.current = undefined;
    const id = crypto.randomUUID();
    requestIdentity.current = id; remember(id); setUncertain(true);
    return run(signal => sendAgentSharedTurn(threadId, { ...input, context, clientRequestId: id }, { signal }), true);
  }
  function recover() {
    const id = requestIdentity.current ?? requestId;
    if (!id) return Promise.resolve(false);
    requestIdentity.current = id;
    return run(signal => getAgentSharedOperation(threadId, id, sharedContextParams(context), { signal }));
  }
  async function stop(messageId = result?.message.id, reason = 'Stopped by the participant.') {
    if (!messageId && !requestIdentity.current) return;
    // Abort/fence the in-flight POST before the explicit server-side stop.
    controller.current.abort(); controller.current = new AbortController();
    const token = ++sequence.current; flight.current = true; setBusy(true); setError(undefined);
    try {
      const id = messageId ?? (await getAgentSharedOperation(threadId, requestIdentity.current!, sharedContextParams(context), { signal: controller.current.signal })).message.id;
      if (!alive.current || token !== sequence.current) return;
      await stopAgentSharedTurn(threadId, id, { context, reason }, { signal: controller.current.signal });
      if (!alive.current || token !== sequence.current) return;
      if (requestIdentity.current) {
        await accept(await getAgentSharedOperation(threadId, requestIdentity.current, sharedContextParams(context), { signal: controller.current.signal }), token);
      } else {
        setUncertain(false);
        await client.invalidateQueries({ queryKey: getListAgentSharedMessagesQueryKey(threadId) });
      }
      // Stop cannot turn an uncertain native save into a confirmed failure.
    } catch (failure) { if (alive.current && token === sequence.current) { setUncertain(true); setError(workspaceError(failure)); } }
    finally { if (alive.current && token === sequence.current) { flight.current = false; setBusy(false); } }
  }
  async function speakTurn(message: string) {
    completed.current = undefined;
    if (!await send({ message, mode: 'shared' })) return null;
    const value = completed.current as AgentSharedTurnResult | undefined;
    return value?.message.status === 'completed' && !value.message.restricted ? value.message.assistantContent ?? null : null;
  }
  React.useEffect(() => {
    // A remembered identity can already be completed. Reconcile it through the
    // actor-private read before deciding whether the workspace must stay blocked.
    // Keep the identity so private reviews/receipts remain recoverable; never resend.
    if (initialRecoveryId.current) void recover();
  }, []);
  return { result, busy, uncertain, error, refreshNotice, requestId, send, recover, stop, speakTurn };
}
